import { logger } from 'app/logging/Logger'
import { DefaultCreateConsultUseCase } from 'app/api/consult/usecase/CreateConsultUseCase'
import { DefaultUpdateConsultUseCase } from 'app/api/consult/usecase/UpdateConsultUseCase'
import { consultRepository, audioRepository } from 'app/di/RepositoryContainer'
import { S3StorageRepository } from 'app/api/consult/repository/StorageRepository'
import { AmplifyDiarizationRepository } from 'app/api/consult/repository/DiarizationRepository'
import { AmplifyTranscriptionRepository } from 'app/api/consult/repository/TranscriptionRepository'
import { entityRepository } from 'app/api/consult/repository/EntityRepository'
import { consultTemplateRepository } from 'app/api/consult/repository/ConsultTemplateRepository'
import { consultPatientRepository } from 'app/api/consult/repository/ConsultPatientRepository'
import { Consult, ConsultState } from 'app/models/domain/consult'
import { AttachmentDto } from 'app/models/generated'

const createConsultUseCase = new DefaultCreateConsultUseCase(
    consultRepository,
    audioRepository,
    new AmplifyDiarizationRepository(),
    new AmplifyTranscriptionRepository(),
    entityRepository,
    consultTemplateRepository,
    consultPatientRepository
)

const updateConsultUseCase = new DefaultUpdateConsultUseCase(consultRepository)

export async function createConsult(
    consult: Consult,
    blob: Blob,
    onComplete: (consultId: string) => void
) {
    await createConsultUseCase.run(consult, blob, onComplete)
}

export async function retryAudioUpload(
    consultId: string,
    audioId: string,
    blob: Blob,
    onComplete: (consultId: string) => void
) {
    await createConsultUseCase.retryAudioUpload(
        consultId,
        audioId,
        blob,
        onComplete,
        // A retry from the History list has no live recording session, so give
        // it its own upload instance rather than the shared singleton, which a
        // concurrent recording could reset mid-upload (ACA-83).
        new S3StorageRepository()
    )
}

export async function updateConsult(
    consult: Consult,
    attachment: AttachmentDto,
    onComplete: (consultId: string) => void
) {
    try {
        await updateConsultUseCase.execute(consult, attachment, onComplete)
    } catch (error) {
        logger.error('Update consult use case failed', error as Error, {
            action: 'update_consult_error',
        })
    }
}

export async function batchReSignAttachments(
    consult: Consult,
    expiredAttachments: AttachmentDto[]
) {
    try {
        await updateConsultUseCase.batchReSignAttachments(
            consult,
            expiredAttachments
        )
    } catch (error) {
        logger.error('Batch re-sign attachments failed', error as Error, {
            action: 'batch_re_sign_error',
        })
    }
}

export async function updateConsultState(
    consult: Consult,
    state: ConsultState
) {
    try {
        await updateConsultUseCase.executeUpdateState(consult, state)
    } catch (error) {
        logger.error('Update consult state failed', error as Error, {
            action: 'update_consult_state_error',
        })
    }
}
